import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  serverStatus, byUrgency, countStatuses, health, diskInfo, timeAgo,
} from "./dashboardHelpers.js";
import { Section, KpiTile, StatusDot } from "./DashboardParts.jsx";
import { ServerCard } from "./DeviceCards.jsx";

// ══════════════════════════════════════════════════════
//  Onglet Serveurs — liste filtrable à gauche, fiche détaillée à droite.
// ══════════════════════════════════════════════════════

export default function ServersTab({ data }) {
  const navigate = useNavigate();
  const { servers, loading, errors } = data;
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const sorted = useMemo(
    () => [...servers].sort(byUrgency(serverStatus, s => s.hostname)),
    [servers]
  );
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter(s => [s.hostname, s.companyName, s.ipAddress, s.os]
      .some(v => (v || "").toLowerCase().includes(q)));
  }, [sorted, query]);

  const selected = filtered.find(s => s.id === selectedId) || filtered[0] || null;
  const c = countStatuses(servers.map(serverStatus));
  const offline = servers.filter(s => s.status !== "ONLINE").length;

  return (
    <div>
      <div className="dash-metrics-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 12, marginBottom: 16 }}>
        <KpiTile label="Serveurs" value={servers.length} />
        <KpiTile label="En ligne" value={servers.length - offline} color={health("OK").color} />
        <KpiTile label="Hors ligne" value={offline} color={offline ? health("CRITICAL").color : "var(--text-3)"} />
        <KpiTile label="À surveiller" value={c.WARNING + c.CRITICAL - offline} sub="disque élevé ou critique" color={c.WARNING ? health("WARNING").color : "var(--text-3)"} />
      </div>

      <div className="itm-card">
        <div className="card-title-row">
          <div>
            <div className="card-title">Serveurs supervisés</div>
            <div className="card-sub">Du plus urgent au plus calme</div>
          </div>
          <button className="btn-itm btn-itm-outline" style={{ padding: "4px 11px", fontSize: 11 }} onClick={() => navigate("/assets")}>
            Voir le parc →
          </button>
        </div>

        <Section loading={loading} error={errors.servers} count={servers.length} empty="Aucun serveur supervisé pour le moment.">
          <div className="dash-split" style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 14, alignItems: "start" }}>
            <div>
              <input className="itm-input" placeholder="Rechercher (nom, client, IP, OS)…" value={query}
                onChange={e => setQuery(e.target.value)} style={{ width: "100%", marginBottom: 8 }} />
              <div style={{ display: "flex", flexDirection: "column", gap: 2, maxHeight: 560, overflowY: "auto" }}>
                {filtered.length === 0 && <div style={{ padding: 14, fontSize: 12.5, color: "var(--text-3)" }}>Aucun résultat.</div>}
                {filtered.map(s => {
                  const d = diskInfo(s);
                  return (
                    <button key={s.id} className={`dash-row-btn ${selected?.id === s.id ? "active" : ""}`} onClick={() => setSelectedId(s.id)}>
                      <StatusDot status={serverStatus(s)} size={9} />
                      <span style={{ minWidth: 0, flex: 1 }}>
                        <span style={{ display: "block", fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.hostname || "—"}</span>
                        <span style={{ display: "block", fontSize: 11, color: "var(--text-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.companyName || "—"}</span>
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: s.status !== "ONLINE" ? "var(--danger)" : d ? d.color : "var(--text-3)" }}>
                        {s.status !== "ONLINE" ? "Hors ligne" : d ? `${d.pct} %` : "—"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ minWidth: 0 }}>
              {selected ? (
                <>
                  <ServerCard server={selected} />
                  <div style={{ fontSize: 11.5, color: "var(--text-3)", marginTop: 8 }}>
                    {selected.status !== "ONLINE" && selected.lastSeenAt ? `Dernière connexion de l'agent ${timeAgo(selected.lastSeenAt)}. ` : ""}
                    Les données proviennent de l'agent Atera et sont rafraîchies toutes les 30 secondes.
                  </div>
                </>
              ) : <div style={{ padding: 30, textAlign: "center", color: "var(--text-3)", fontSize: 13 }}>Sélectionnez un serveur.</div>}
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
}